// cspell:ignore paymentmethod

import { theme } from '@garnish/constants';
import { hostedFieldPostMessageSnippet } from '@sg/garnish';

import { getBaseUrl } from '@order/utils';

import { APPLE_PAY_REQUEST } from './constants';

export function getApplePaySource(props: ApplePayProps) {
  const { stripeKey, clientSecret, amountToCapture } = props;

  return {
    baseUrl: getBaseUrl(),
    html: `
    <html lang="en">
    <head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
        <title>Apple Pay</title>
    
        <script src="https://js.stripe.com/v3/"></script>
        <script type="text/javascript">

          // ─── React Connection ────────────────────────────────────────

          ${hostedFieldPostMessageSnippet}

          // ─── Stripe Initialization ───────────────────────────────────

          const stripeKey = "${stripeKey}";
          const clientSecret = "${clientSecret}";
          const stripe = Stripe(stripeKey);
          const elements = stripe.elements();

          // ─── Payment Request Setup ───────────────────────────────────

          postMessage({ log: 'Setting up payment request' });
          const paymentRequest = stripe.paymentRequest({
            country: '${APPLE_PAY_REQUEST.country}',
            currency: '${APPLE_PAY_REQUEST.currency}',
            total: { label: '${
              APPLE_PAY_REQUEST.label
            }', amount: ${amountToCapture} },
            requestPayerName: ${APPLE_PAY_REQUEST.requestPayerName},
            requestPayerEmail: ${APPLE_PAY_REQUEST.requestPayerEmail},
          });

          void paymentRequest.canMakePayment().then((requestResult) => {
            if (!requestResult?.applePay) { 
              postMessage({ log: 'Apple Pay disabled' });
              return;
            }

            const paymentRequestButton = elements.create('paymentRequestButton', { paymentRequest });
            paymentRequestButton.mount('#payment-request-button');

            postMessage({ log: 'Apple Pay enabled', applePayEnabled: true });

            paymentRequest.on('paymentmethod', async (paymentResult) => {
              const paymentMethodId = paymentResult?.paymentMethod?.id;
              
              postMessage({ log: 'Captured payment method id: ' + paymentMethodId });
  
              const { error: confirmError } = await stripe.confirmSetup({
                clientSecret,
                redirect: 'if_required',
                confirmParams: {
                  payment_method: paymentMethodId,
                  return_url: "${getBaseUrl()}",
                },
              });
  
              if (confirmError) {
                postMessage({ log: 'Failed to setup apple pay: ' + confirmError, error: true });
                paymentResult.complete('fail');
  
                return;
              }
  
              paymentResult.complete('success');
              postMessage({ log: 'Succeeded to setup apple pay', paymentMethodId });
            });
          });
        </script>

        <style>
          html {
            background-color: ${theme.colors.KHAKI_3};
          }
        </style>
      </head>
    
      <body>
        <div id="payment-request-button"></div>
      </body>
    </html>
  `,
  };
}

// ─── Types ─────────────────────────────────────────────────────────────

type ApplePayProps = Readonly<{
  stripeKey: string | undefined;
  clientSecret: string | undefined;
  amountToCapture: number;
}>;
